"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../../types";
import EditableText from "../../../core/EditableText";
import { LocationContent, buildFullAddress, generateMapEmbedUrl } from "../types";
import { PiMapPin, PiPlus, PiMinus, PiNavigationArrow, PiLink } from "react-icons/pi";
import ConfirmDialog from "@/components/modals/ConfirmDialog";

// ============================================================
// VARIANT 4: MULTI-LOCATION
// Grid layout with multiple office locations
// ============================================================

interface MultiLocationProps extends Omit<SectionContentProps, "content"> {
  content: LocationContent;
}

// Sample locations for demo
const sampleLocations: LocationContent[] = [
  {
    variant: "full-width",
    address: "123 Business Ave",
    city: "San Francisco",
    state: "CA",
    country: "USA",
    postalCode: "94102",
    isPrimary: true,
  },
  {
    variant: "full-width",
    address: "456 Broadway",
    city: "New York",
    state: "NY",
    country: "USA",
    postalCode: "10012",
  },
  {
    variant: "full-width",
    address: "789 Oxford Street",
    city: "London",
    country: "UK",
    postalCode: "W1D 1BT",
  },
];

export default function MultiLocation({ content: propContent, onContentChange, isSelected }: MultiLocationProps) {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Default locations or from content
  const locations = content.locations?.length ? content.locations : sampleLocations;
  const [selectedLocation, setSelectedLocation] = useState<LocationContent | null>(
    locations.find((l) => l.isPrimary) || locations[0] || null
  );
  const [locationToRemove, setLocationToRemove] = useState<number | null>(null);

  // Get map URL for selected location
  const selectedFullAddress = selectedLocation ? buildFullAddress(selectedLocation) : "";
  const selectedMapUrl = selectedLocation?.mapEmbedUrl || generateMapEmbedUrl(selectedFullAddress, content.mapProvider || "google");

  const handleLocationSelect = (location: LocationContent) => {
    setSelectedLocation(location);
    // Update selected location in content if needed
    if (onContentChange) {
      const updatedLocations = locations.map((l) => ({
        ...l,
        isPrimary: l === location,
      }));
      onContentChange({ ...content, locations: updatedLocations });
    }
  };

  const handleGetDirections = (location: LocationContent) => {
    const addr = buildFullAddress(location);
    const destination = encodeURIComponent(addr);
    window.open(`https://www.google.com/maps/dir/?destination=${destination}`, "_blank");
  };

  const handleMapUrlEdit = () => {
    const url = prompt("Enter Google Maps Embed URL:", selectedMapUrl);
    if (url && selectedLocation && onContentChange) {
      const updatedLocations = locations.map((l) =>
        l === selectedLocation ? { ...l, mapEmbedUrl: url } : l
      );
      onContentChange({ ...content, locations: updatedLocations });
    }
  };

  const handleAddLocation = () => {
    const addr = prompt("Enter new location address (Street, City, State, Country):");
    if (addr) {
      const parts = addr.split(",").map((s) => s.trim());
      const newLocation: LocationContent = {
        variant: "full-width",
        address: parts[0] || "",
        city: parts[1] || "",
        state: parts[2] || "",
        country: parts[3] || "",
        postalCode: parts[4] || "",
      };
      if (onContentChange) {
        onContentChange({ ...content, locations: [...locations, newLocation] });
      }
    }
  };

  const handleRemoveLocation = (index: number) => {
    setLocationToRemove(index);
  };

  const confirmRemoveLocation = () => {
    if (locationToRemove === null) return;
    const index = locationToRemove;
    setLocationToRemove(null);

    const updatedLocations = locations.filter((_, i) => i !== index);
    if (onContentChange) {
      onContentChange({ ...content, locations: updatedLocations });
    }
    if (selectedLocation === locations[index]) {
      setSelectedLocation(updatedLocations[0] || null);
    }
  };

  return (
    <>
      <section className="location-section-multi" aria-label="Multiple locations">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            <PiMapPin className="w-4 h-4" />
            <EditableText
              value={content.subtitle || "Multiple Locations"}
              onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
              as="span"
              placeholder="Subtitle"
            />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            <EditableText
              value={content.title || "Our Locations"}
              onChange={(v) => onContentChange?.({ ...content, title: v })}
              as="span"
              placeholder="Title"
            />
          </h2>
          {content.description && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mt-3">
              <EditableText
                value={content.description}
                onChange={(v) => onContentChange?.({ ...content, description: v })}
                as="span"
                placeholder="Description"
              />
            </p>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Location List */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Our Offices</h3>
              {isSelected && onContentChange && (
                <button
                  onClick={handleAddLocation}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PiPlus className="w-4 h-4" />
                  Add
                </button>
              )}
            </div>
            <div className="space-y-3">
              {locations.map((location, index) => {
                const locAddress = buildFullAddress(location);
                return (
                  <button
                    key={index}
                    onClick={() => handleLocationSelect(location)}
                    className={`w-full text-left p-4 rounded-xl transition-all ${selectedLocation === location
                      ? "bg-blue-50 border-2 border-blue-500"
                      : "bg-white border-2 border-slate-200 hover:border-slate-300"
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-slate-900">
                            <EditableText
                              value={location.city ? `${location.city} Office` : `Location ${index + 1}`}
                              onChange={(v) => {
                                if (onContentChange) {
                                  const updated = [...locations];
                                  updated[index] = { ...location, city: v.split(" ")[0] };
                                  onContentChange({ ...content, locations: updated });
                                }
                              }}
                              as="span"
                              placeholder={`Location ${index + 1}`}
                            />
                          </span>
                          {location.isPrimary && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                              HQ
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          <EditableText
                            value={locAddress || "Add address"}
                            onChange={(v) => {
                              if (onContentChange) {
                                const parts = v.split(",").map((s) => s.trim());
                                const updated = [...locations];
                                updated[index] = {
                                  ...location,
                                  address: parts[0] || "",
                                  city: parts[1] || "",
                                  state: parts[2] || "",
                                  postalCode: parts[3] || "",
                                  country: parts[4] || "",
                                };
                                onContentChange({ ...content, locations: updated });
                              }
                            }}
                            as="span"
                            placeholder="Full address"
                          />
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedLocation === location ? (
                          <PiMinus className="w-5 h-5 text-blue-600" />
                        ) : (
                          <PiPlus className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Map & Selected Location Info */}
          <div className="lg:col-span-2">
            {/* Map */}
            {selectedLocation && (
              <>
                <div className="bg-slate-100 rounded-2xl overflow-hidden h-[350px] md:h-[400px] relative">
                  {selectedFullAddress || selectedLocation.mapEmbedUrl ? (
                    <>
                      <iframe
                        src={selectedMapUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0, borderRadius: "1rem" }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map showing ${selectedLocation.city || "location"}`}
                        aria-label="Interactive map"
                      />
                      {isSelected && (
                        <button
                          onClick={handleMapUrlEdit}
                          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-1"
                        >
                          <PiLink className="w-3 h-3" />
                          Edit URL
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-slate-200 rounded-2xl">
                      <PiMapPin className="w-10 h-10 text-slate-400 mb-3" />
                      <p className="text-slate-500 text-sm">Select a location to view map</p>
                    </div>
                  )}
                </div>

                {/* Selected Location Details */}
                <div className="mt-6 p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        <EditableText
                          value={selectedLocation.city ? `${selectedLocation.city} Office` : "Main Office"}
                          onChange={(v) => {
                            if (onContentChange) {
                              const updated = locations.map((l) =>
                                l === selectedLocation ? { ...l, city: v.split(" ")[0] } : l
                              );
                              onContentChange({ ...content, locations: updated });
                            }
                          }}
                          as="span"
                          placeholder="Location name"
                        />
                      </h3>
                      <p className="text-slate-500 mt-1">
                        <EditableText
                          value={selectedFullAddress}
                          onChange={(v) => {
                            if (onContentChange) {
                              const parts = v.split(",").map((s) => s.trim());
                              const updated = locations.map((l) =>
                                l === selectedLocation
                                  ? {
                                    ...l,
                                    address: parts[0] || "",
                                    city: parts[1] || "",
                                    state: parts[2] || "",
                                    postalCode: parts[3] || "",
                                    country: parts[4] || "",
                                  }
                                  : l
                              );
                              onContentChange({ ...content, locations: updated });
                            }
                          }}
                          as="span"
                          placeholder="Full address"
                        />
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleGetDirections(selectedLocation)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <PiNavigationArrow className="w-4 h-4" />
                        Get Directions
                      </button>
                      {isSelected && locations.length > 1 && (
                        <button
                          onClick={() => handleRemoveLocation(locations.indexOf(selectedLocation))}
                          className="px-3 py-2 text-red-600 text-sm hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>

    <ConfirmDialog
      isOpen={locationToRemove !== null}
      onClose={() => setLocationToRemove(null)}
      onConfirm={confirmRemoveLocation}
      title="Remove Location"
      message="Remove this location?"
      confirmText="Remove"
      cancelText="Cancel"
      variant="danger"
    />
    </>
  );
}
